<template>
    <div class="quick-add-wizard">
        <div
            v-if="showScheduledChangeNotice"
            class="scheduled-changes-quick-add"
        >
            <scheduled-changes-for-quick-add
                :differences="differences"
                :next-billing-date="nextBillingAt"
                :billing-frequency="billingFrequency"
                :currency-symbol="currencySymbol"
                :scheduled-changes-estimate-total="scheduledChangesEstimateTotal"
                :current-plan="plan"
                @close-notice="closeScheduledChangeNotice"
            />
        </div>

        <div
            v-else-if="showReactivationNotice"
            class="reactivation-notice-quick-add"
        >
            <reactivation-notice-for-quick-add
                :next-term-renewal-date="nextBillingAt"
            />
        </div>

        <div v-else class="quick-add-grid">
            <div class="left-column">
                <div class="stage-indicator-container">
                    <StageIndicator
                        :active-stage="selectedWizardStage"
                        :stages="stages"
                        @go-to-stage="goToStage"
                    />
                </div>

                <slot />
            </div>

            <div class="right-column">
                <div class="plan-overview">
                    <plan-overview
                        class="plan-overview"
                        data-qa="plan-overview"
                        :stage="selectedWizardStage"
                        :selected-tier="selectedTier"
                        :plan="plan"
                        :line-items="lineItems"
                        :currency-symbol="currencySymbol"
                        :period-unit="billingFrequency"
                        :quick-add-type="typeOfAddon"
                        :total="total"
                        :pricing-tiers="contactPricingTiers"
                        :current-tier="currentTier"
                        :change-estimate-loading="changeEstimateLoading"
                        :change-estimate="changeEstimate"
                        :confirmation-loading-status="confirmationLoadingStatus"
                        :subscription-details="subscriptionDetails"
                        @continue-with-selection="continueWithSelection"
                        @change-confirmed="confirmChange"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>

const IN_PROGRESS = 'inProgress';
const COMPLETE = 'complete';
const LOADING = 'loading';
const SUCCESS = 'success';
const ERROR = 'error';
const FINAL_QUOTE = 'FINAL_QUOTE';
const CONTACTS_QUICK_ADD = 'CONTACTS_QUICK_ADD';
const SELECTION_STAGE = 0;
const VERIFY_STAGE = 1;
const PAYMENT_CONFIRMATION = 'PAYMENT_CONFIRMATION';
const AMPLITUDE_ERROR_STATUS = 'ERROR';
const AMPLITUDE_SUCCESS_STATUS = 'SUCCESS';
const AMPLITUDE_FINAL_QUOTE_STEP = 'Final Quote';
const AMPLITUDE_PAYMENT_CONFIRMATION_STEP = 'Payment Confirmation';

import amplitude from '@/components/util/analytics/amplitude';
import ScheduledChangesForQuickAdd from './ScheduledChangesForQuickAdd.vue';
import ReactivationNoticeForQuickAdd from './ReactivationNoticeForQuickAdd.vue';
import PlanOverview from './PlanOverview.vue';
import StageIndicator from '@/components/broadcast/StageIndicator';
import axios from 'axios';

export default {
    components: {
        PlanOverview,
        ScheduledChangesForQuickAdd,
        ReactivationNoticeForQuickAdd,
        StageIndicator,
    },

    props: {
        typeOfAddon: String,
        selectedWizardStage: Number,
        selectedTier: Number,
        currentTier: Number,
        contactDetails: Object,
        subscriptionDetails: Object,
        confirmationLoadingStatus: String,
    },

    data() {
        return {
            changeEstimateLoading: false,
            errorLoadingChangeEstimate: false,
            changeEstimate: {
                subscriptionStatus: '',
                subscriptionPeriodUnit: '',
                currencyCode: '',
                nextBillingAtTimeMillis: 0,
                nextTermEstimate: {
                    lineItems: [],
                    taxAmount: '0',
                    total: '0',
                },
                proratedEstimate: {
                    lineItems: [],
                    taxAmount: '0',
                    total: '0',
                },
            },
        };
    },

    computed: {
        plan() {
            return this.estimate?.estimateLineItems?.find(({ type }) => type === 'PLAN') || {};
        },

        total() {
            return this.estimate.total;
        },

        differences() {
            return this.subscriptionDetails.differences;
        },

        nextBillingAt() {
            return this.subscriptionDetails.nextBillingAt;
        },

        billingFrequency() {
            return this.subscriptionDetails.billingFrequency;
        },

        currencySymbol() {
            return this.subscriptionDetails.currencySymbol;
        },

        scheduledChangesEstimateTotal() {
            return this.subscriptionDetails.scheduledChangesEstimateTotal;
        },

        showScheduledChangeNotice() {
            return this.subscriptionDetails.showScheduledChangesNotice;
        },

        showReactivationNotice() {
            return this.subscriptionDetails.scheduledToCancel;
        },

        lineItems() {
            return this.subscriptionDetails.lineItems;
        },

        estimate() {
            return this.subscriptionDetails.estimate;
        },

        contactPricingTiers() {
            return this.contactDetails.contactPricingTiers;
        },

        stages() {
            return this.stagesMap.map((stage, index) => {
                const isComplete = index < this.selectedWizardStage;

                return {
                    label: stage.labelKey,
                    status: isComplete ? COMPLETE : IN_PROGRESS,
                };
            });
        },

        stagesMap() {
            return [
                { labelKey: 'Add contacts' },
                { labelKey: 'Confirm change' },
            ];
        },

        isConfirmationLoadingStatusSuccess() {
            return this.confirmationLoadingStatus === SUCCESS;
        },

        isConfirmationLoadingStatusLoading() {
            return this.confirmationLoadingStatus === LOADING;
        },
    },

    methods: {
        closeScheduledChangeNotice() {
            this.$emit('close-scheduled-change-notice');
        },

        async continueWithSelection() {
            this.changeEstimateLoading = true;
            this.errorLoadingChangeEstimate = false;
            this.$emit('set-selected-wizard-stage', this.selectedWizardStage + 1);

            try {
                const quantity = this.contactPricingTiers.at(this.selectedTier).maxUnits;

                if (quantity !== null) {
                    const changeEstimateResponse = await axios.get(`/app/myAccount/changeEstimate?type=${this.typeOfAddon}&quantity=${quantity}`);

                    if (changeEstimateResponse.data !== null && Object.keys(changeEstimateResponse.data).length !== 0) {
                        this.changeEstimate = changeEstimateResponse.data.subscriptionChangeEstimate;
                    } else {
                        throw new Error('Empty body returned from KSM when fetching change estimate');
                    }
                } else {
                    throw new Error('Selected quantity is null');
                }

                this.sendAmplitudeEvent(AMPLITUDE_FINAL_QUOTE_STEP, AMPLITUDE_SUCCESS_STATUS);
            } catch (e) {
                this.errorLoadingChangeEstimate = true;
                this.$emit('set-selected-wizard-stage', SELECTION_STAGE);
                this.$emit('error-loading-change-estimate');
                this.logError(FINAL_QUOTE, e.message, e.stack);
                this.sendAmplitudeEvent(AMPLITUDE_FINAL_QUOTE_STEP, AMPLITUDE_ERROR_STATUS);
            } finally {
                this.setChangeEstimate();
                this.changeEstimateLoading = false;
            }
        },

        async confirmChange() {
            this.$emit('set-confirmation-loading-status', LOADING);

            try {
                const quantity = this.contactPricingTiers.at(this.selectedTier).maxUnits;

                if (quantity !== null) {
                    const updateContactsQuantityResponse = await axios.post(`/app/myAccount/additional-contacts?quantity=${quantity}`);

                    if (updateContactsQuantityResponse.data !== null && Object.keys(updateContactsQuantityResponse.data).length !== 0) {
                        this.subscriptionInfo = updateContactsQuantityResponse.data;
                        this.$emit('set-confirmation-loading-status', SUCCESS);
                        this.$emit('set-selected-wizard-stage', this.selectedWizardStage + 1);
                    } else {
                        throw new Error('Empty body returned from KSM');
                    }
                }

                this.sendAmplitudeEvent(AMPLITUDE_PAYMENT_CONFIRMATION_STEP, AMPLITUDE_SUCCESS_STATUS);
            } catch (e) {
                this.$emit('set-selected-wizard-stage', VERIFY_STAGE);
                this.$emit('set-confirmation-loading-status', ERROR);
                this.logError(PAYMENT_CONFIRMATION, e.message, e.stack);
                this.sendAmplitudeEvent(AMPLITUDE_PAYMENT_CONFIRMATION_STEP, AMPLITUDE_ERROR_STATUS);
            } finally {
                this.setChangeEstimate();
            }
        },

        setChangeEstimate() {
            this.$emit('change-estimate-loaded', this.changeEstimate);
        },

        goToStage(index) {
            if (index >= this.selectedWizardStage || this.isConfirmationLoadingStatusSuccess || this.isConfirmationLoadingStatusLoading) {
                return;
            }

            this.selectedWizardStage = index;
            this.$emit('set-selected-wizard-stage', this.selectedWizardStage);
        },

        logError(step, error, details) {
            const errorBody = {
                source: CONTACTS_QUICK_ADD,
                step,
                error,
                details,
            };

            axios.post('/app/myAccount/logError', errorBody);
        },

        sendAmplitudeEvent(action, status) {
            amplitude.infusionsoft.track(amplitude.keap.events.SUBSCRIPTION_MANAGEMENT_QUICK_ADD, {
                Event: action,
                Type: this.typeOfAddon.toLowerCase(),
                Status: status,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.scheduled-changes-quick-add {
    width: 70%;
    max-width: px-to-rem(912px);
    min-width: px-to-rem(327px);
    margin: 0 auto;
}

.reactivation-notice-quick-add {
    width: 70%;
    max-width: px-to-rem(912px);
    min-width: px-to-rem(327px);
    margin: 0 auto;
}

.stage-indicator-container {
    padding: 0 $spacing-300 $spacing-300 $spacing-300;
    display: contents;
}

.stage-indicator {
    align-self: center;
    margin-bottom: $spacing-300
}

.quick-add-grid {
    display: flex;
    width: 70%;
    margin: 0 auto;
    padding: $spacing-250;
    gap: $spacing-200;
    justify-content: center;
}

.left-column {
    max-width: px-to-rem(608px);
    display: flex;
    flex-direction: column;
    flex: 1;
}
.right-column {
    flex: 0 0 auto;
}

@media screen and ($small) {
    ::v-deep .label {
        display: inline-block;
    }

    .plan-overview {
        min-width: px-to-rem(343px)
    }

    .quick-add-grid {
        flex-direction: column;
        align-items: center;
        width: 100%;
    }

    .left-column {
        max-width: none;
    }
}
</style>
